import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

import type { VendorAvailabilityData, MealPeriods, ProductType } from '../types';
import { MealPeriod, QuickAction } from '../types';
import { MEAL_PERIODS } from '../constants';
import { WindowStatusEnum, ProductEnum, SubmissionStatusEnum } from '@/api';
import { DayjsFormat } from '@/helpers';

type UseAvailabilityState = {
  availableDates: Set<string>;
  selectedMealPeriods: Set<MealPeriod>;
  acknowledgementChecked: boolean;
  isDirty: boolean;
  activeQuickAction: QuickAction | undefined;
  toggleDate: (date: string) => void;
  applyQuickAction: (action: QuickAction) => void;
  toggleMealPeriod: (period: MealPeriod) => void;
  setAcknowledgement: (checked: boolean) => void;
  reset: (data: VendorAvailabilityData) => void;
  getDatesPayload: () => { date: string; available: boolean }[];
  getMealPeriodsPayload: () => MealPeriods;
};

const initDates = (data: VendorAvailabilityData, productType?: ProductType): Set<string> => {
  if (
    data.windowStatus === WindowStatusEnum.LOCKED &&
    productType === ProductEnum.POPUP &&
    data.submissionStatus === SubmissionStatusEnum.NOT_SUBMITTED
  ) {
    return new Set();
  }

  return new Set(data.dates.filter((d) => d.available).map((d) => d.date));
};

const initMealPeriods = (data: VendorAvailabilityData): Set<MealPeriod> => {
  if (!data.mealPeriods) return new Set();
  const set = new Set<MealPeriod>();
  for (const period of MEAL_PERIODS) {
    const isEligible = data.eligibleMealPeriods?.[period] === true;
    if (data.mealPeriods[period] && isEligible) set.add(period);
  }
  return set;
};

export const useAvailabilityState = (
  initialData: VendorAvailabilityData,
  month: number | null,
  year: number | null,
  productType: ProductType
): UseAvailabilityState => {
  const targetMonth = month || 0;
  const targetYear = year || 0;
  const daysInMonth = dayjs(`${targetYear}-${String(targetMonth)}`).daysInMonth();

  const [availableDates, setAvailableDates] = useState<Set<string>>(() =>
    initDates(initialData, productType)
  );
  const [selectedMealPeriods, setSelectedMealPeriods] = useState<Set<MealPeriod>>(() =>
    initMealPeriods(initialData)
  );
  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const toggleDate = useCallback((date: string) => {
    setAvailableDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
    setIsDirty(true);
  }, []);

  const applyQuickAction = useCallback(
    (action: QuickAction) => {
      if (!month || !year) return;

      const next = new Set<string>();

      for (let day = 1; day <= daysInMonth; day++) {
        const dateObj = new Date(year, month - 1, day);
        const dayOfWeek = dateObj.getDay();
        const dateStr = dayjs(dateObj).format(DayjsFormat.CALENDAR_DATE);

        if (action === QuickAction.EVERYDAY) {
          next.add(dateStr);
        } else if (action === QuickAction.MON_FRI && dayOfWeek >= 1 && dayOfWeek <= 5) {
          next.add(dateStr);
        }
        // QuickAction.UNAVAILABLE leaves the set empty
      }

      setAvailableDates(next);
      setIsDirty(true);
    },
    [month, year]
  );

  const toggleMealPeriod = useCallback((period: MealPeriod) => {
    setSelectedMealPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(period)) {
        next.delete(period);
      } else {
        next.add(period);
      }
      return next;
    });

    setIsDirty(true);
  }, []);

  const setAcknowledgement = useCallback((checked: boolean) => {
    setAcknowledgementChecked(checked);
    setIsDirty(true);
  }, []);

  const reset = useCallback(
    (data: VendorAvailabilityData) => {
      setAvailableDates(initDates(data, productType));
      setSelectedMealPeriods(initMealPeriods(data));
      setAcknowledgementChecked(false);
      setIsDirty(false);
    },
    [productType]
  );

  const getDatesPayload = useCallback(() => {
    const payload: { date: string; available: boolean }[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${targetYear}-${String(targetMonth).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`;
      payload.push({ date: dateStr, available: availableDates.has(dateStr) });
    }

    return payload;
  }, [availableDates, targetMonth, targetYear]);

  const getMealPeriodsPayload = useCallback(
    (): MealPeriods => ({
      breakfast: selectedMealPeriods.has(MealPeriod.BREAKFAST),
      lunch: selectedMealPeriods.has(MealPeriod.LUNCH),
      dinner: selectedMealPeriods.has(MealPeriod.DINNER),
      appetizers: false,
    }),
    [selectedMealPeriods]
  );

  const activeQuickAction = useMemo<QuickAction | undefined>(() => {
    if (!month || !year) return undefined;
    if (availableDates.size === 0) return QuickAction.UNAVAILABLE;

    const allDates: string[] = [];
    const weekdayDates: string[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month - 1, day);
      const dateStr = dayjs(dateObj).format(DayjsFormat.CALENDAR_DATE);
      allDates.push(dateStr);
      const dayOfWeek = dateObj.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        weekdayDates.push(dateStr);
      }
    }

    if (availableDates.size === allDates.length && allDates.every((d) => availableDates.has(d))) {
      return QuickAction.EVERYDAY;
    }

    if (
      availableDates.size === weekdayDates.length &&
      weekdayDates.every((d) => availableDates.has(d))
    ) {
      return QuickAction.MON_FRI;
    }

    return undefined;
  }, [availableDates, daysInMonth, month, year]);

  return useMemo(
    () => ({
      availableDates,
      selectedMealPeriods,
      acknowledgementChecked,
      isDirty,
      activeQuickAction,
      toggleDate,
      applyQuickAction,
      toggleMealPeriod,
      setAcknowledgement,
      reset,
      getDatesPayload,
      getMealPeriodsPayload,
    }),
    [
      availableDates,
      selectedMealPeriods,
      acknowledgementChecked,
      isDirty,
      activeQuickAction,
      toggleDate,
      applyQuickAction,
      toggleMealPeriod,
      setAcknowledgement,
      reset,
      getDatesPayload,
      getMealPeriodsPayload,
    ]
  );
};
